import { useEffect, useState } from "react";
import { Wallet, Plus, Trash2, FileText, MessageCircle, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const DAKAR_PHONE = "221774419060";

const KIND_LABEL = { cash_china: "🛫 Cash Chine", exchange: "🔄 Change devises" };

const EMPTY_DEP = {
  client_name: "", client_phone: "", client_email: "",
  deposit_amount: "", deposit_currency: "XOF",
  delivery_amount: "", delivery_currency: "CNY", exchange_rate: "",
  pickup_city: "Yiwu", kind: "cash_china", notes: "",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function buildDakarWa(msg) {
  return `https://wa.me/${DAKAR_PHONE}?text=${encodeURIComponent(msg)}`;
}

export default function TransfersPanel() {
  const [requests, setRequests] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [dep, setDep] = useState(EMPTY_DEP);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [r, d] = await Promise.all([
        api.get("/admin/transfer-requests"),
        api.get("/admin/deposits"),
      ]);
      setRequests(r.data || []);
      setDeposits(d.data || []);
    } catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const saveDeposit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...dep,
        deposit_amount: parseFloat(dep.deposit_amount),
        delivery_amount: dep.delivery_amount ? parseFloat(dep.delivery_amount) : null,
        exchange_rate: dep.exchange_rate ? parseFloat(dep.exchange_rate) : null,
      };
      if (!payload.client_email) delete payload.client_email;
      const { data } = await api.post("/admin/deposits", payload);
      setDep(EMPTY_DEP);
      load();
      alert(`Dépôt enregistré. Référence : ${data.reference}\nLe reçu PDF est prêt à imprimer.`);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const delDeposit = async (id) => {
    if (!window.confirm("Supprimer ce dépôt ?")) return;
    await api.delete(`/admin/deposits/${id}`);
    load();
  };

  const delRequest = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/admin/transfer-requests/${id}`);
    load();
  };

  const waFromRequest = (r) => {
    const msg = `Bonjour ${r.name},\n\nNous avons bien reçu votre demande ${KIND_LABEL[r.kind] || ""} (réf. ${r.reference}) pour ${Math.round(r.amount).toLocaleString("fr-FR")} ${r.from_currency}.\n\nMerci de passer au bureau Négosino Dakar pour finaliser l'opération.\n\n— L'équipe Négosino`;
    const phone = String(r.phone || "").replace(/[^\d]/g, "");
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-8" data-testid="admin-transfers">
      {err && <p className="text-sm text-red-500">{err}</p>}

      {/* Deposits */}
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={saveDeposit} className="card-flat p-6 space-y-3 lg:col-span-1" data-testid="admin-deposit-form">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><Wallet size={18} /> Enregistrer un dépôt</h3>
          <div className="flex gap-2 flex-wrap">
            {[{k:"cash_china",l:"🛫 Cash Chine"},{k:"exchange",l:"🔄 Change"}].map(o=>(
              <button key={o.k} type="button" onClick={()=>setDep({...dep, kind:o.k})} className={`text-xs font-bold px-3 py-1.5 rounded-full ${dep.kind===o.k?"bg-[var(--accent)] text-white":"bg-[var(--bg-soft)] text-[var(--ink-soft)]"}`}>{o.l}</button>
            ))}
          </div>
          <input required placeholder="Nom du client" value={dep.client_name} onChange={(e)=>setDep({...dep,client_name:e.target.value})} className="input-field" />
          <input placeholder="Téléphone" value={dep.client_phone} onChange={(e)=>setDep({...dep,client_phone:e.target.value})} className="input-field" />
          <input type="email" placeholder="Email (optionnel)" value={dep.client_email} onChange={(e)=>setDep({...dep,client_email:e.target.value})} className="input-field" />
          <div className="flex gap-2">
            <input required type="number" step="100" placeholder="Montant déposé" value={dep.deposit_amount} onChange={(e)=>setDep({...dep,deposit_amount:e.target.value})} className="input-field flex-1" data-testid="deposit-amount" />
            <select value={dep.deposit_currency} onChange={(e)=>setDep({...dep,deposit_currency:e.target.value})} className="input-field w-24">
              <option value="XOF">FCFA</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="CNY">CNY</option>
            </select>
          </div>
          <div className="flex gap-2">
            <input type="number" step="1" placeholder="À recevoir" value={dep.delivery_amount} onChange={(e)=>setDep({...dep,delivery_amount:e.target.value})} className="input-field flex-1" />
            <select value={dep.delivery_currency} onChange={(e)=>setDep({...dep,delivery_currency:e.target.value})} className="input-field w-24">
              <option value="CNY">CNY</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="XOF">FCFA</option>
            </select>
          </div>
          <input type="number" step="0.01" placeholder={`Taux : 1 ${dep.delivery_currency} = ? ${dep.deposit_currency}`} value={dep.exchange_rate} onChange={(e)=>setDep({...dep,exchange_rate:e.target.value})} className="input-field" />
          {dep.kind === "cash_china" && (
            <select value={dep.pickup_city} onChange={(e)=>setDep({...dep,pickup_city:e.target.value})} className="input-field">
              <option value="Yiwu">Retrait à Yiwu</option><option value="Guangzhou">Retrait à Guangzhou</option><option value="Autre">Autre</option>
            </select>
          )}
          <textarea placeholder="Notes internes" value={dep.notes} onChange={(e)=>setDep({...dep,notes:e.target.value})} className="input-field min-h-[70px]" />
          <button type="submit" className="btn-primary w-full justify-center" data-testid="deposit-submit"><Plus size={16}/> Enregistrer & générer le reçu PDF</button>
        </form>

        <div className="lg:col-span-2 space-y-3">
          <h3 className="font-display text-xl font-bold">Dépôts récents ({deposits.length})</h3>
          {deposits.length === 0 ? (
            <div className="card-flat p-8 text-center text-sm text-[var(--ink-soft)]">Aucun dépôt enregistré.</div>
          ) : deposits.map((d) => (
            <div key={d.id} className="card-flat p-4" data-testid={`deposit-${d.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono font-bold">{d.reference}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--accent)] text-white px-2 py-0.5 rounded-full">{KIND_LABEL[d.kind]}</span>
                <span className="text-xs text-[var(--ink-soft)]">{formatDate(d.created_at)}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-sm">
                <div><span className="text-[var(--ink-soft)] text-xs">Client</span><p className="font-bold">{d.client_name}</p></div>
                <div><span className="text-[var(--ink-soft)] text-xs">Déposé</span><p className="font-bold text-[var(--accent)]">{Math.round(d.deposit_amount).toLocaleString("fr-FR")} {d.deposit_currency}</p></div>
                {d.delivery_amount != null && (
                  <div><span className="text-[var(--ink-soft)] text-xs">À remettre</span><p className="font-bold">{Math.round(d.delivery_amount).toLocaleString("fr-FR")} {d.delivery_currency}</p></div>
                )}
                {d.pickup_city && <div><span className="text-[var(--ink-soft)] text-xs">Retrait</span><p className="font-bold">{d.pickup_city}</p></div>}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/deposits/${d.reference}/receipt.pdf`} target="_blank" rel="noopener noreferrer" className="btn-outline !py-1.5 !px-3 text-xs" data-testid={`deposit-pdf-${d.id}`}><FileText size={13}/> Reçu PDF</a>
                <button onClick={()=>delDeposit(d.id)} className="text-red-500 p-2 hover:bg-red-50 rounded"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Requests */}
      <div>
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><Clock size={18}/> Demandes en ligne ({requests.length})</h3>
        <p className="text-sm text-[var(--ink-soft)] mt-1">Les clients envoient leur demande via la page /transfert. Vous les rappelez ensuite.</p>
        <div className="mt-4 space-y-3">
          {requests.length === 0 ? (
            <div className="card-flat p-8 text-center text-sm text-[var(--ink-soft)]">Aucune demande pour le moment.</div>
          ) : requests.map((r) => (
            <div key={r.id} className="card-flat p-4" data-testid={`request-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono font-bold text-sm">{r.reference}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--secondary)] text-white px-2 py-0.5 rounded-full">{KIND_LABEL[r.kind]}</span>
                <span className="text-xs text-[var(--ink-soft)]">{formatDate(r.created_at)}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-sm">
                <div><span className="text-[var(--ink-soft)] text-xs">Client</span><p className="font-bold">{r.name}</p></div>
                <div><span className="text-[var(--ink-soft)] text-xs">Téléphone</span><p className="font-bold">{r.phone}</p></div>
                <div><span className="text-[var(--ink-soft)] text-xs">Montant</span><p className="font-bold">{Math.round(r.amount).toLocaleString("fr-FR")} {r.from_currency} → {r.to_currency}</p></div>
                {r.pickup_city && <div><span className="text-[var(--ink-soft)] text-xs">Retrait</span><p className="font-bold">{r.pickup_city}</p></div>}
              </div>
              {r.notes && <p className="text-sm text-[var(--ink-soft)] mt-2 italic">« {r.notes} »</p>}
              <div className="flex gap-2 mt-3 flex-wrap">
                <a href={waFromRequest(r)} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] hover:bg-[#1ea855] text-white !py-1.5 !px-3 text-xs rounded inline-flex items-center gap-1.5 font-bold"><MessageCircle size={13}/> Rappeler le client</a>
                <button onClick={()=>delRequest(r.id)} className="text-red-500 p-2 hover:bg-red-50 rounded"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
